<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import Button from '@/components/Button.vue'
import { useImageZoom } from '@/composables/useImageZoom'

const props = defineProps({
  items: { type: Array, required: true },
  startId: { type: [Number, String], default: null },
})

const emit = defineEmits(['close'])

const imageRef = ref(null)
const {
  rotation,
  wrapperStyle,
  imageStyle,
  zoom,
  onWheel,
  onDoubleClick,
  rotate,
  reset,
} = useImageZoom(imageRef)

const index = ref(
  Math.max(
    0,
    props.items.findIndex(item => item.id === props.startId)
  )
)

const current = computed(() => props.items[index.value] || {})
const isImage = computed(() => current.value.fileType === 'image')
const isVideo = computed(() => current.value.fileType === 'video')
const isAudio = computed(() => current.value.fileType === 'audio')
const hasSiblings = computed(() => props.items.length > 1)

const timeFormatter = new Intl.DateTimeFormat('ru', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})
const readableTime = computed(() =>
  current.value.createdAt
    ? timeFormatter.format(new Date(current.value.createdAt))
    : ''
)

const goTo = step => {
  const next = index.value + step
  if (next < 0 || next >= props.items.length) return

  index.value = next
  reset()
}

const onKeydown = event => {
  if (event.key === 'Escape') emit('close')
  if (event.key === 'ArrowLeft') goTo(-1)
  if (event.key === 'ArrowRight') goTo(1)
}

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown))
</script>

<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-50 flex flex-col select-none bg-n-background"
      @click="emit('close')"
    >
      <header
        class="z-10 flex items-center justify-between h-16 gap-2 px-6 border-b bg-n-background border-n-weak"
        @click.stop
      >
        <div class="flex flex-col min-w-0 basis-60 shrink-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ current.senderName }}
          </span>
          <span class="text-xs text-n-slate-11">{{ readableTime }}</span>
        </div>

        <span class="flex-1 text-sm font-medium text-center truncate text-n-slate-12">
          {{ current.fileName }}
        </span>

        <div class="flex items-center gap-1 shrink-0">
          <template v-if="isImage">
            <Button
              v-tooltip.bottom="$t('CONVERSATIONS.GALLERY.ZOOM_IN')"
              slate
              ghost
              icon="i-lucide-zoom-in"
              @click="zoom(0.2)"
            />
            <Button
              v-tooltip.bottom="$t('CONVERSATIONS.GALLERY.ZOOM_OUT')"
              slate
              ghost
              icon="i-lucide-zoom-out"
              @click="zoom(-0.2)"
            />
            <Button
              v-tooltip.bottom="$t('CONVERSATIONS.GALLERY.ROTATE_LEFT')"
              slate
              ghost
              icon="i-lucide-rotate-ccw"
              @click="rotate('ccw')"
            />
            <Button
              v-tooltip.bottom="$t('CONVERSATIONS.GALLERY.ROTATE_RIGHT')"
              slate
              ghost
              icon="i-lucide-rotate-cw"
              @click="rotate('cw')"
            />
          </template>
          <a
            v-tooltip.bottom="$t('CONVERSATIONS.GALLERY.DOWNLOAD')"
            :href="current.dataUrl"
            :download="current.fileName"
            target="_blank"
            rel="noopener"
            class="flex items-center justify-center rounded-lg size-10 text-n-slate-12 hover:bg-n-alpha-2"
          >
            <span class="i-lucide-download size-4" />
          </a>
          <Button
            v-tooltip.bottom="$t('CONVERSATIONS.GALLERY.CLOSE')"
            slate
            ghost
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>
      </header>

      <main class="flex items-stretch flex-1 min-h-0">
        <div class="flex items-center justify-center w-16 shrink-0">
          <Button
            v-if="hasSiblings"
            blue
            faded
            lg
            icon="i-lucide-chevron-left"
            :disabled="index === 0"
            @click.stop="goTo(-1)"
          />
        </div>

        <div class="flex items-center justify-center flex-1 min-w-0 overflow-hidden">
          <div
            v-if="isImage"
            :style="wrapperStyle"
            class="flex items-center justify-center origin-center"
            :class="
              rotation % 180 === 0
                ? 'size-full'
                : 'w-[calc(100dvh-8rem)] h-[calc(100dvw-8rem)]'
            "
          >
            <img
              ref="imageRef"
              :key="current.id"
              :src="current.dataUrl"
              :alt="current.fileName || ''"
              :style="imageStyle"
              class="object-contain max-w-full max-h-full duration-100 ease-in-out"
              @click.stop
              @dblclick.stop="onDoubleClick"
              @wheel.prevent.stop="onWheel"
            />
          </div>

          <video
            v-else-if="isVideo"
            :key="current.id"
            :src="current.dataUrl"
            controls
            autoplay
            class="object-contain max-w-full max-h-full"
            @click.stop
          />

          <audio
            v-else-if="isAudio"
            :key="current.id"
            :src="current.dataUrl"
            controls
            autoplay
            class="w-full max-w-md"
            @click.stop
          />
        </div>

        <div class="flex items-center justify-center w-16 shrink-0">
          <Button
            v-if="hasSiblings"
            blue
            faded
            lg
            icon="i-lucide-chevron-right"
            :disabled="index === items.length - 1"
            @click.stop="goTo(1)"
          />
        </div>
      </main>

      <footer
        v-if="hasSiblings"
        class="z-10 flex items-center justify-center h-12 border-t border-n-weak"
        @click.stop
      >
        <span
          class="px-3 py-1 text-sm font-medium rounded-md bg-n-alpha-2 text-n-slate-12"
        >
          {{ `${index + 1} / ${items.length}` }}
        </span>
      </footer>
    </div>
  </teleport>
</template>
